import type { WebSocket } from 'partysocket';
import type { WebSocketMessage, BlueprintType, ConversationMessage, PresenceMember } from '@/api-types';
import { logger } from '@/utils/logger';
import { getFileType } from '@/utils/string';
import { getPreviewUrl } from '@/lib/utils';
import {
    setFileGenerating,
    appendFileChunk,
    setFileCompleted,
    setAllFilesCompleted,
    updatePhaseFileStatus,
} from './file-state-helpers';
import {
    createAIMessage,
    handleRateLimitError,
    handleStreamingMessage,
    appendToolEvent,
    appendActivityLine,
    type ChatMessage,
} from './message-helpers';
import { completeStages } from './project-stage-helpers';
import { sendWebSocketMessage } from './websocket-helpers';
import type { AgentRole } from 'shared/agents/activityDisplay';
import type { FileType, PhaseTimelineItem } from '../hooks/use-chat';
import type { TakeoverRequest, TakeoverStatus } from './takeover';
import { toast } from 'sonner';

export interface ImageGenerationState {
    active: boolean;
    completed: number;
    total: number;
}

export interface HandleMessageDeps {
    // State setters
    setFiles: React.Dispatch<React.SetStateAction<FileType[]>>;
    setPhaseTimeline: React.Dispatch<React.SetStateAction<PhaseTimelineItem[]>>;
    setProjectStages: React.Dispatch<React.SetStateAction<any[]>>;
    setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    setBlueprint: React.Dispatch<React.SetStateAction<BlueprintType | undefined>>;
    setQuery: React.Dispatch<React.SetStateAction<string | undefined>>;
    setPreviewUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
    setTotalFiles: React.Dispatch<React.SetStateAction<number | undefined>>;
    setIsRedeployReady: React.Dispatch<React.SetStateAction<boolean>>;
    setIsPreviewDeploying: React.Dispatch<React.SetStateAction<boolean>>;
    setImageGeneration: React.Dispatch<React.SetStateAction<ImageGenerationState>>;
    setIsThinking: React.Dispatch<React.SetStateAction<boolean>>;
    setIsInitialStateRestored: React.Dispatch<React.SetStateAction<boolean>>;
    setShouldRefreshPreview: React.Dispatch<React.SetStateAction<boolean>>;
    setIsDeploying: React.Dispatch<React.SetStateAction<boolean>>;
    setCloudflareDeploymentUrl: React.Dispatch<React.SetStateAction<string>>;
    setDeploymentError: React.Dispatch<React.SetStateAction<string | undefined>>;
    setIsGenerationPaused: React.Dispatch<React.SetStateAction<boolean>>;
    setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
    setIsPhaseProgressActive: React.Dispatch<React.SetStateAction<boolean>>;
    // Org collaboration: live presence roster, the current driver, and the name of
    // whoever a non-driver was just soft-blocked behind (for the "take over" prompt).
    setPresenceMembers: React.Dispatch<React.SetStateAction<PresenceMember[]>>;
    setCurrentDriverId: React.Dispatch<React.SetStateAction<string | null>>;
    setDrivingBlockedBy: React.Dispatch<React.SetStateAction<string | null>>;
    // Consent-gated takeover: the inbound consent prompt (real user) and the
    // operator's own request status.
    setTakeoverRequest: React.Dispatch<React.SetStateAction<TakeoverRequest | null>>;
    setTakeoverStatus: React.Dispatch<React.SetStateAction<TakeoverStatus | null>>;

    /**
     * Fix-cycle narration state (persists across events via a ref). A "cycle"
     * is a round of work triggered by a user request AFTER the initial build —
     * the case where the owner pasted an error and couldn't tell anything
     * happened. `active` gates narration so the initial generation (already
     * covered by the PhaseTimeline) stays quiet.
     */
    fixCycleRef: React.MutableRefObject<{ id: string; active: boolean }>;

    // Current state
    isInitialStateRestored: boolean;
    blueprint: BlueprintType | undefined;
    query: string | undefined;
    bootstrapFiles: FileType[];
    files: FileType[];
    phaseTimeline: PhaseTimelineItem[];
    previewUrl: string | undefined;
    projectStages: any[];
    isGenerating: boolean;
    urlChatId: string | undefined;
    
    // Functions
    updateStage: (stageId: string, updates: any) => void;
    sendMessage: (message: ConversationMessage) => void;
    loadBootstrapFiles: (files: FileType[]) => void;
    onDebugMessage?: (
        type: 'error' | 'warning' | 'info' | 'websocket',
        message: string,
        details?: string,
        source?: string,
        messageType?: string,
        rawMessage?: unknown
    ) => void;
    onTerminalMessage?: (log: { 
        id: string; 
        content: string; 
        type: 'command' | 'stdout' | 'stderr' | 'info' | 'error' | 'warn' | 'debug'; 
        timestamp: number; 
        source?: string 
    }) => void;
}

/**
 * Sockets that already auto-resumed generation once. Every cf_agent_state
 * broadcast carries shouldBeGenerating=true for the whole build — re-sending
 * generate_all on each one made client and server ping-pong state broadcasts
 * for the build's entire duration. Auto-resume is only meant to kick a
 * dropped build back to life once per connection.
 */
const autoResumedSockets = new WeakSet<WebSocket>();

export function createWebSocketMessageHandler(deps: HandleMessageDeps) {
    const extractTextContent = (content: ConversationMessage['content']): string => {
        if (!content) return '';
        if (typeof content === 'string') return content;
        if (Array.isArray(content)) {
            return content
                .map(c => (c && 'type' in c && c.type === 'text') ? c.text : '')
                .join(' ')
                .trim();
        }
        return '';
    };
    return (websocket: WebSocket, message: WebSocketMessage) => {
        const {
            setFiles,
            setPhaseTimeline,
            setProjectStages,
            setMessages,
            setBlueprint,
            setQuery,
            setPreviewUrl,
            setTotalFiles,
            setIsRedeployReady,
            setIsPreviewDeploying,
            setImageGeneration,
            setIsThinking,
            setIsInitialStateRestored,
            setShouldRefreshPreview,
            setIsDeploying,
            setCloudflareDeploymentUrl,
            setDeploymentError,
            setIsGenerationPaused,
            setIsGenerating,
            setIsPhaseProgressActive,
            setPresenceMembers,
            setCurrentDriverId,
            setDrivingBlockedBy,
            setTakeoverRequest,
            setTakeoverStatus,
            fixCycleRef,
            isInitialStateRestored,
            blueprint,
            query,
            bootstrapFiles,
            files,
            phaseTimeline,
            previewUrl,
            projectStages,
            isGenerating,
            urlChatId,
            updateStage,
            sendMessage,
            loadBootstrapFiles,
            onDebugMessage,
            onTerminalMessage,
        } = deps;

        /**
         * Emit a plain-language narration line into the CURRENT fix cycle's
         * rolling card. No-op unless a cycle is active (so initial generation,
         * covered by the PhaseTimeline, stays quiet). Bypasses the sendMessage
         * whitelist by writing the `activity-` card directly.
         */
        const narrate = (line: { text: string; role: AgentRole; tone: 'progress' | 'done' | 'attention' }) => {
            if (!fixCycleRef.current.active) return;
            setMessages(prev => appendActivityLine(prev, fixCycleRef.current.id, line));
        };

        // Log messages except for frequent ones
        if (message.type !== 'file_chunk_generated' && message.type !== 'cf_agent_state' && message.type.length <= 50) {
            logger.info('received message', message.type, message);
            onDebugMessage?.('websocket', 
                `${message.type}`,
                JSON.stringify(message, null, 2),
                'WebSocket',
                message.type,
                message
            );
        }
        
        switch (message.type) {
            case 'cf_agent_state': {
                const { state } = message;
                logger.debug('🔄 Agent state update received:', state);

                if (!isInitialStateRestored) {
                    logger.debug('📥 Performing initial state restoration');
                    
                    if (state.blueprint && !blueprint) {
                        setBlueprint(state.blueprint);
                        updateStage('blueprint', { status: 'completed' });
                    }

                    if (state.query && !query) {
                        setQuery(state.query);
                    }

                    if (state.templateDetails?.files && bootstrapFiles.length === 0) {
                        loadBootstrapFiles(state.templateDetails.files);
                    }

                    if (state.generatedFilesMap && files.length === 0) {
                        setFiles(
                            Object.values(state.generatedFilesMap).map((file: any) => ({
                                filePath: file.filePath,
                                fileContents: file.fileContents,
                                isGenerating: false,
                                needsFixing: false,
                                hasErrors: false,
                                language: getFileType(file.filePath),
                            })),
                        );
                    }

                    if (state.generatedPhases && state.generatedPhases.length > 0 && phaseTimeline.length === 0) {
                        logger.debug('📋 Restoring phase timeline:', state.generatedPhases);
                        const timeline = state.generatedPhases.map((phase: any, index: number) => ({
                            id: `phase-${index}`,
                            name: phase.name,
                            description: phase.description,
                            status: phase.completed ? 'completed' as const : 'generating' as const,
                            files: phase.files.map((filesConcept: any) => {
                                const file = state.generatedFilesMap?.[filesConcept.path];
                                return {
                                    path: filesConcept.path,
                                    purpose: filesConcept.purpose,
                                    status: (file ? 'completed' as const : 'generating' as const),
                                    contents: file?.fileContents
                                };
                            }),
                            timestamp: Date.now(),
                        }));
                        setPhaseTimeline(timeline);
                    }
                    
                    updateStage('bootstrap', { status: 'completed' });
                    
                    if (state.blueprint) {
                        updateStage('blueprint', { status: 'completed' });
                    }
                    
                    if (state.generatedFilesMap && Object.keys(state.generatedFilesMap).length > 0) {
                        updateStage('code', { status: 'completed' });
                        updateStage('validate', { status: 'completed' });
                    }

                    setIsInitialStateRestored(true);

                    if (state.generatedFilesMap && Object.keys(state.generatedFilesMap).length > 0 && 
                        urlChatId !== 'new') {
                        logger.debug('🚀 Requesting preview deployment for existing chat with files');
                        sendWebSocketMessage(websocket, 'preview');
                    }
                }

                if (state.shouldBeGenerating && !autoResumedSockets.has(websocket)) {
                    autoResumedSockets.add(websocket);
                    logger.debug('🔄 shouldBeGenerating=true detected, auto-resuming generation (once per connection)');
                    updateStage('code', { status: 'active' });

                    logger.debug('📡 Sending auto-resume generate_all message');
                    sendWebSocketMessage(websocket, 'generate_all');
                } else if (!state.shouldBeGenerating) {
                    const codeStage = projectStages.find((stage: any) => stage.id === 'code');
                    if (codeStage?.status === 'active' && !isGenerating) {
                        if (state.generatedFilesMap && Object.keys(state.generatedFilesMap).length > 0) {
                            updateStage('code', { status: 'completed' });
                            updateStage('validate', { status: 'completed' });

                            if (!previewUrl) {
                                logger.debug('🚀 Generated files exist but no preview URL - auto-deploying preview');
                                sendWebSocketMessage(websocket, 'preview');
                            }
                        }
                    }
                }

                logger.debug('✅ Agent state update processed');
                break;
            }

            case 'conversation_state': {
                const { state } = message;
                const history: ReadonlyArray<ConversationMessage> = state?.runningHistory ?? [];
                logger.debug('Received conversation_state with messages:', history.length);

                const restoredMessages: ChatMessage[] = history.reduce<ChatMessage[]>((acc, msg) => {
                    if (msg.role !== 'user' && msg.role !== 'assistant') return acc;
                    const text = extractTextContent(msg.content);
                    if (!text || text.includes('<Internal Memo>')) return acc;

                    const convId = msg.conversationId;
                    const isArchive = msg.role === 'assistant' && convId.startsWith('archive-');

                    acc.push({
                        role: msg.role,
                        conversationId: convId,
                        content: isArchive ? 'previous history was compacted' : text,
                    });
                    return acc;
                }, []);

                if (restoredMessages.length > 0) {
                    logger.debug('Replacing messages with conversation_state history:', restoredMessages.length);
                    setMessages(restoredMessages);
                }
                break;
            }

            case 'file_generating': {
                setFiles((prev) => setFileGenerating(prev, message.filePath));
                break;
            }

            case 'file_chunk_generated': {
                setFiles((prev) => appendFileChunk(prev, message.filePath, message.chunk));
                break;
            }

            case 'file_generated': {
                setFiles((prev) => setFileCompleted(prev, message.file.filePath, message.file.fileContents));
                setPhaseTimeline((prev) => updatePhaseFileStatus(
                    prev,
                    message.file.filePath,
                    'completed',
                    message.file.fileContents
                ));
                break;
            }

            case 'file_regenerated': {
                setIsRedeployReady(true);
                setFiles((prev) => setFileCompleted(prev, message.file.filePath, message.file.fileContents));
                setPhaseTimeline((prev) => updatePhaseFileStatus(
                    prev,
                    message.file.filePath,
                    'completed',
                    message.file.fileContents
                ));
                break;
            }

            case 'file_regenerating': {
                setFiles((prev) => setFileGenerating(prev, message.filePath, 'File being regenerated...'));
                setPhaseTimeline((prev) => updatePhaseFileStatus(prev, message.filePath, 'generating'));
                break;
            }

            case 'generation_started': {
                updateStage('code', { status: 'active' });
                setTotalFiles(message.totalFiles);
                break;
            }

            case 'improvement_suggestions': {
                const suggestionsMessage: ChatMessage = {
                    role: 'assistant',
                    conversationId: 'improvement-suggestions',
                    content: message.message,
                    ui: { suggestions: message.suggestions },
                };
                sendMessage(suggestionsMessage);
                break;
            }

            case 'image_generation_consent': {
                const consentMessage: ChatMessage = {
                    role: 'assistant',
                    conversationId: 'image-consent',
                    content: message.message,
                    ui: {
                        imageConsent: {
                            images: message.images,
                            count: message.count,
                            totalSparks: message.totalSparks,
                        },
                    },
                };
                sendMessage(consentMessage);
                break;
            }

            case 'generation_complete': {
                setIsRedeployReady(true);
                setFiles((prev) => setAllFilesCompleted(prev));
                setProjectStages((prev) => completeStages(prev, ['code', 'validate', 'fix']));

                // Safety net: if a fix cycle is still open (no deployment_completed
                // arrived), close it with a gentler "ready" line so later work
                // never attaches to a stale card.
                if (fixCycleRef.current.active) {
                    narrate({
                        text: 'Your changes are ready in the preview.',
                        role: 'builder',
                        tone: 'done',
                    });
                    fixCycleRef.current = { ...fixCycleRef.current, active: false };
                }

                sendMessage(createAIMessage('generation-complete', 'Code generation has been completed.'));
                setIsPhaseProgressActive(false);
                break;
            }

            case 'deployment_started': {
                setIsPreviewDeploying(true);
                break;
            }

            case 'deployment_completed': {
                setIsPreviewDeploying(false);
                const finalPreviewURL = getPreviewUrl(message.previewURL, message.tunnelURL);
                setPreviewUrl(finalPreviewURL);
                // The payoff the owner missed: a redeploy after a fix cycle IS
                // the "your change is live, try it again" moment (the preview
                // auto-refreshes ~1s later). Narrate it, then close the cycle.
                if (fixCycleRef.current.active) {
                    narrate({
                        text: 'Done — your update is live in the preview. Try it again now.',
                        role: 'builder',
                        tone: 'done',
                    });
                    fixCycleRef.current = { ...fixCycleRef.current, active: false };
                }
                break;
            }

            case 'deployment_failed': {
                toast.error(`Error: ${message.message}`);
                break;
            }

            case 'code_reviewed': {
                const reviewData = message.review;
                const totalIssues = reviewData?.filesToFix?.reduce((count: number, file: any) => 
                    count + file.issues.length, 0) || 0;
                
                let reviewMessage = 'Code review complete';
                if (reviewData?.issuesFound) {
                    reviewMessage = `Code review complete - ${totalIssues} issue${totalIssues !== 1 ? 's' : ''} found across ${reviewData.filesToFix?.length || 0} file${reviewData.filesToFix?.length !== 1 ? 's' : ''}`;
                } else {
                    reviewMessage = 'Code review complete - no issues found';
                }
                
                sendMessage(createAIMessage('code_reviewed', reviewMessage));
                break;
            }

            case 'runtime_error_found': {
                logger.info('Runtime error found in sandbox', message.errors);
                
                onDebugMessage?.('error', 
                    `Runtime Error (${message.count} errors)`,
                    message.errors.map((e: any) => `${e.message}\nStack: ${e.stack || 'N/A'}`).join('\n\n'),
                    'Runtime Detection'
                );
                break;
            }

            case 'code_reviewing': {
                const totalIssues =
                    (message.staticAnalysis?.lint?.issues?.length || 0) +
                    (message.staticAnalysis?.typecheck?.issues?.length || 0) +
                    (message.runtimeErrors.length || 0);

                updateStage('validate', { status: 'active' });

                if (totalIssues > 0) {
                    updateStage('fix', { status: 'active', metadata: `Fixing ${totalIssues} issues` });
                    
                    const errorDetails = [
                        `Lint Issues: ${JSON.stringify(message.staticAnalysis?.lint?.issues)}`,
                        `Type Errors: ${JSON.stringify(message.staticAnalysis?.typecheck?.issues)}`,
                        `Runtime Errors: ${JSON.stringify(message.runtimeErrors)}`,
                        `Client Errors: ${JSON.stringify(message.clientErrors)}`,
                    ].filter(Boolean).join('\n');
                    
                    onDebugMessage?.('warning', 
                        `Generation Issues Found (${totalIssues} total)`,
                        errorDetails,
                        'Code Generation'
                    );
                }
                break;
            }

            case 'phase_generating': {
                updateStage('validate', { status: 'completed' });
                updateStage('fix', { status: 'completed' });
                // A phase carrying user suggestions is a FIX CYCLE (the user
                // asked for a change / reported a bug after the initial build).
                // Open a fresh narration card so the work is visible — the exact
                // thing that was missing when the owner pasted an error and saw
                // nothing happen. Initial generation carries no suggestions and
                // stays quiet (the PhaseTimeline covers it).
                if (message.userSuggestions && message.userSuggestions.length > 0) {
                    fixCycleRef.current = { id: `${Date.now()}`, active: true };
                    narrate({
                        text: 'On it — planning the changes you asked for.',
                        role: 'architect',
                        tone: 'progress',
                    });
                }
                sendMessage(createAIMessage('phase_generating', message.message));
                setIsThinking(true);
                setIsPhaseProgressActive(true);
                break;
            }

            case 'phase_generated': {
                sendMessage(createAIMessage('phase_generated', message.message));
                setIsThinking(false);
                setIsPhaseProgressActive(false);
                break;
            }

            case 'phase_implementing': {
                narrate({
                    text: message.phase?.name
                        ? `Building the changes: ${message.phase.name}.`
                        : 'Building the changes.',
                    role: 'builder',
                    tone: 'progress',
                });
                sendMessage(createAIMessage('phase_implementing', message.message));
                updateStage('code', { status: 'active' });
                
                if (message.phase) {
                    setPhaseTimeline(prev => {
                        const existingPhase = prev.find(p => p.name === message.phase.name);
                        if (existingPhase) {
                            logger.debug('Phase already exists in timeline:', message.phase.name);
                            return prev;
                        }
                        
                        const newPhase = {
                            id: `${message.phase.name}-${Date.now()}`,
                            name: message.phase.name,
                            description: message.phase.description,
                            files: message.phase.files?.map((f: any) => ({
                                path: f.path,
                                purpose: f.purpose,
                                status: 'generating' as const,
                            })) || [],
                            status: 'generating' as const,
                            timestamp: Date.now()
                        };
                        
                        logger.debug('Added new phase to timeline:', message.phase.name);
                        return [...prev, newPhase];
                    });
                }
                break;
            }

            case 'phase_validating': {
                sendMessage(createAIMessage('phase_validating', message.message));
                updateStage('validate', { status: 'active' });
                
                setPhaseTimeline(prev => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                        const lastPhase = updated[updated.length - 1];
                        lastPhase.status = 'validating';
                        logger.debug(`Phase validating: ${lastPhase.name}`);
                    }
                    return updated;
                });
                setIsPreviewDeploying(false);
                setIsPhaseProgressActive(false);
                break;
            }

            case 'phase_validated': {
                sendMessage(createAIMessage('phase_validated', message.message));
                updateStage('validate', { status: 'completed' });
                break;
            }

            case 'phase_implemented': {
                sendMessage(createAIMessage('phase_implemented', message.message));

                updateStage('code', { status: 'completed' });
                setIsRedeployReady(true);
                setIsPhaseProgressActive(false);
                
                if (message.phase) {
                    setPhaseTimeline(prev => {
                        const updated = [...prev];
                        if (updated.length > 0) {
                            const lastPhase = updated[updated.length - 1];
                            lastPhase.status = 'completed';
                            lastPhase.files = lastPhase.files.map(f => ({ ...f, status: 'completed' as const }));
                            logger.debug(`Phase completed: ${lastPhase.name}`);
                        }
                        return updated;
                    });
                }

                logger.debug('🔄 Scheduling preview refresh in 1 second after deployment completion');
                setTimeout(() => {
                    logger.debug('🔄 Triggering preview refresh after deployment completion');
                    setShouldRefreshPreview(true);
                    
                    setTimeout(() => {
                        setShouldRefreshPreview(false);
                    }, 100);
                    
                    onDebugMessage?.('info',
                        'Preview Auto-Refresh Triggered',
                        `Preview refreshed 1 second after deployment completion`,
                        'Preview Auto-Refresh'
                    );
                }, 1000);
                break;
            }

            case 'generation_stopped': {
                setIsGenerating(false);
                setIsGenerationPaused(true);
                sendMessage(createAIMessage('generation_stopped', message.message));
                break;
            }

            case 'generation_resumed': {
                setIsGenerating(true);
                setIsGenerationPaused(false);
                sendMessage(createAIMessage('generation_resumed', message.message));
                break;
            }

            case 'cloudflare_deployment_started': {
                setIsDeploying(true);
                sendMessage(createAIMessage('cloudflare_deployment_started', message.message));
                break;
            }

            case 'cloudflare_deployment_completed': {
                setIsDeploying(false);
                setCloudflareDeploymentUrl(message.deploymentUrl);
                setDeploymentError('');
                setIsRedeployReady(false);
                
                sendMessage(createAIMessage('cloudflare_deployment_completed', `Your project has been permanently deployed to Cloudflare Workers: ${message.deploymentUrl}`));
                
                onDebugMessage?.('info', 
                    'Deployment Completed - Redeploy Reset',
                    `Deployment URL: ${message.deploymentUrl}\nPhase count at deployment: ${phaseTimeline.length}\nRedeploy button disabled until next phase`,
                    'Redeployment Management'
                );
                break;
            }

            case 'cloudflare_deployment_error': {
                setIsDeploying(false);
                setDeploymentError(message.error || 'Unknown deployment error');
                setCloudflareDeploymentUrl('');
                setIsRedeployReady(true);
                
                sendMessage(createAIMessage('cloudflare_deployment_error', `Deployment failed: ${message.error}\n\nYou can try deploying again.`));

                toast.error(`Error: ${message.error}`);
                
                onDebugMessage?.('error', 
                    'Deployment Failed - State Reset',
                    `Error: ${message.error}\nDeployment button reset for retry`,
                    'Deployment Error Recovery'
                );
                break;
            }

            case 'github_export_started': {
                sendMessage(createAIMessage('github_export_started', message.message));
                break;
            }

            case 'github_export_progress': {
                sendMessage(createAIMessage('github_export_progress', message.message));
                break;
            }

            case 'github_export_completed': {
                sendMessage(createAIMessage('github_export_completed', message.message));
                break;
            }

            case 'github_export_error': {
                sendMessage(createAIMessage('github_export_error', `GitHub export failed: ${message.error}`));

                toast.error(`Error: ${message.error}`);
                
                break;
            }

            case 'conversation_response': {
                // Use concrete conversationId when available; otherwise use placeholder id
                let conversationId = message.conversationId ?? 'conversation_response';

                // If a concrete id arrives later, update placeholder once
                if (message.conversationId) {
                    const convId = message.conversationId;
                    setMessages(prev => {
                        const genericIdx = prev.findIndex(m => m.role === 'assistant' && m.conversationId === 'conversation_response');
                        if (genericIdx !== -1) {
                            return prev.map((m, i) => i === genericIdx ? { ...m, conversationId: convId } : m);
                        }
                        return prev;
                    });
                    conversationId = convId;
                }

                const isArchive = conversationId.startsWith('archive-');
                const placeholder = 'previous history was compacted';

                if (message.tool) {
                    const tool = message.tool;
                    setMessages(prev => appendToolEvent(prev, conversationId, { name: tool.name, status: tool.status, args: tool.args, actor: tool.actor }));
                    break;
                }

                if (message.isStreaming) {
                    setMessages(prev => handleStreamingMessage(prev, conversationId, isArchive ? placeholder : message.message, false));
                    break;
                }

                setMessages(prev => {
                    const idx = prev.findIndex(m => m.role === 'assistant' && m.conversationId === conversationId);
                    if (idx !== -1) return prev.map((m, i) => i === idx ? { ...m, content: (isArchive ? placeholder : message.message) } : m);
                    return [...prev, createAIMessage(conversationId, isArchive ? placeholder : message.message)];
                });
                break;
            }

            case 'terminal_output': {
                // Handle terminal output from server
                if (onTerminalMessage) {
                    const terminalLog = {
                        id: `terminal-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                        content: message.output,
                        type: message.outputType as 'stdout' | 'stderr' | 'info',
                        timestamp: message.timestamp
                    };
                    onTerminalMessage(terminalLog);
                }
                break;
            }

            case 'server_log': {
                // Handle server logs
                if (onTerminalMessage) {
                    const serverLog = {
                        id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                        content: message.message,
                        type: message.level as 'info' | 'warn' | 'error' | 'debug',
                        timestamp: message.timestamp,
                        source: message.source
                    };
                    onTerminalMessage(serverLog);
                }
                break;
            }

            case 'error': {
                const errorData = message;
                setMessages(prev => [
                    ...prev,
                    createAIMessage(`error_${Date.now()}`, errorData.error)
                ]);
                
                onDebugMessage?.(
                    'error',
                    'WebSocket Error',
                    errorData.error,
                    'WebSocket',
                    'error',
                    errorData
                );
                break;
            }

            case 'rate_limit_error': {
                const errorData = message.error;
                const rateLimitMessage = handleRateLimitError(
                    errorData.details,
                    onDebugMessage
                );
                setMessages(prev => [...prev, rateLimitMessage]);

                break;
            }

            case 'conversation_cleared': {
                // Reset the chat to a tool-event entry confirming the clear (upstream parity).
                setMessages(() => appendToolEvent([], 'conversation_cleared', {
                    name: message.message || 'conversation reset',
                    status: 'success',
                }));
                break;
            }

            case 'command_executing': {
                // Surface the commands being run in the terminal panel.
                if (onTerminalMessage) {
                    onTerminalMessage({
                        id: `command-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                        content: message.commands.join('\n'),
                        type: 'command',
                        timestamp: Date.now(),
                    });
                }
                break;
            }

            case 'image_generation_started': {
                setImageGeneration({ active: true, completed: 0, total: message.count });
                sendMessage(createAIMessage(
                    `image_generation_started_${Date.now()}`,
                    `Generating ${message.count} image asset${message.count !== 1 ? 's' : ''}…`,
                ));
                break;
            }

            case 'image_generation_progress': {
                setImageGeneration({
                    active: true,
                    completed: message.index,
                    total: message.total,
                });
                onDebugMessage?.(
                    'info',
                    `Image generated (${message.index}/${message.total})`,
                    `${message.path} via ${message.provider}\n${message.url}`,
                    'Image Generation',
                );
                break;
            }

            case 'image_generation_completed': {
                setImageGeneration({ active: false, completed: 0, total: 0 });
                if (message.images.length > 0) {
                    const list = message.images
                        .map((image) => `• ${image.purpose}: ${image.url}`)
                        .join('\n');
                    sendMessage(createAIMessage(
                        `image_generation_completed_${Date.now()}`,
                        `${message.message}\n${list}`,
                    ));
                    // Images now generate concurrently with coding, so the
                    // preview may have already deployed while assets were still
                    // rendering as placeholders. Refresh it so the freshly
                    // generated images swap in. PULSE the flag — leaving it
                    // stuck true makes PreviewIframe reload forever (its effect
                    // re-fires on every loaded state while the flag holds).
                    setShouldRefreshPreview(true);
                    setTimeout(() => {
                        setShouldRefreshPreview(false);
                    }, 100);
                }
                break;
            }

            case 'image_generation_error': {
                onDebugMessage?.(
                    'error',
                    'Image Generation Error',
                    message.path ? `${message.path}: ${message.error}` : message.error,
                    'Image Generation',
                    'error',
                    message,
                );
                break;
            }

            // Known informational / placeholder server events with no client-side UI
            // action yet. Initial state restoration is handled by 'cf_agent_state'.
            // Wiring these into the UI (model settings, static-analysis panel,
            // screenshot gallery, deterministic-fix progress, etc.) is planned feature
            // work for the full Dreamforge push — see worker/api/websocketTypes.ts
            // (M3 commit-2b note). They are acknowledged explicitly here so the
            // `default` branch's warning stays reserved for genuinely unexpected types.
            case 'presence_update':
                setPresenceMembers(message.members);
                setCurrentDriverId(message.currentDriverUserId);
                // The seat is now open (driver left / released) — drop any stale
                // "someone is driving — take over" banner so it can't get stuck.
                if (message.currentDriverUserId === null) {
                    setDrivingBlockedBy(null);
                }
                break;
            case 'driving_blocked':
                // Soft single-driver block: surface who's driving so the UI can
                // offer "take over".
                setDrivingBlockedBy(message.currentDriverName);
                break;
            case 'takeover_request':
                // This viewer IS the real user a privileged operator wants to take
                // over: raise the consent prompt (role-only).
                setTakeoverRequest({
                    requestId: message.requestId,
                    operatorRole: message.operatorRole,
                    isAgent: message.isAgent,
                    reasonUser: message.reasonUser,
                    expiresAt: message.expiresAt,
                });
                break;
            case 'takeover_resolved':
                // This viewer IS the operator: track our request's lifecycle.
                if (message.outcome === 'granted') {
                    setTakeoverStatus(null); // we now drive (presence_update confirms)
                } else if (message.outcome === 'pending') {
                    setTakeoverStatus({ kind: 'waiting', expiresAt: message.expiresAt });
                } else {
                    setTakeoverStatus({ kind: message.outcome }); // 'denied' | 'timed_out'
                }
                break;

            case 'agent_connected':
            case 'template_updated':
            case 'blueprint_updated':
            case 'project_name_updated':
            case 'usage_updated':
            case 'preview_force_refresh':
            case 'model_configs_info':
            case 'static_analysis_results':
            case 'deterministic_code_fix_started':
            case 'deterministic_code_fix_completed':
            case 'screenshot_capture_started':
            case 'screenshot_capture_success':
            case 'screenshot_capture_error':
            case 'screenshot_analysis_result':
                break;

            default:
                logger.warn('Unhandled message:', message);
        }
    };
}
